import { PropertyList, PropertyItem } from "@/components/ui/property-list";
import { useEffect, useState } from "react";

import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import { dohvatiDokumente, type DokumentDjelatnika } from "@/lib/dokumenti";
import { datum } from "@/lib/format";
import {
  dohvatiOsposobljavanja, dohvatiRadnika, punoIme, statusRadnika,
  STATUS_OSPOSOBLJAVANJA, VRSTA_UGOVORA,
  type Osposobljavanje, type Radnik,
} from "@/lib/radnici";
import { danaDo, oznakaDozvole, oznakaLijecnickog, type Oznaka } from "@/lib/status";

const HEALTH_CODES = new Set(["ra2_medical_fitness", "pur_medical_certificate"]);
type Training = Osposobljavanje & { povijesno?: boolean };
type Snapshot = {
  key: string;
  worker?: Radnik;
  documents: DokumentDjelatnika[];
  trainings: Training[];
  errors: string[];
};

function StateRow({ title, label, until }: { title: string; label: Oznaka; until?: string | null }) {
  const tone = label.ton === "bad" ? "bad" : label.ton === "warn" || label.tekst === "Nema podatka" ? "warn" : undefined;
  return <PropertyItem label={title} inline tone={tone}>
    <Badge ton={label.ton}>{label.tekst}</Badge>
    {until && <span className="text-xs">Vrijedi do <span className="broj">{datum(until)}</span></span>}
  </PropertyItem>;
}

function documentState(document: DokumentDjelatnika, active: boolean): Oznaka {
  if (!active || document.status === "superseded") return { tekst: "Arhiva", ton: "idle" };
  if (!document.verificiran) return { tekst: "Nije potvrđeno", ton: "idle" };
  if (document.trajno) return { tekst: "Trajno", ton: "ok" };
  if (document.expires_on && danaDo(document.expires_on, new Date()) < 0) return { tekst: "Isteklo", ton: "bad" };
  if (document.expiry_required && !document.expires_on) return { tekst: "Nedostaje rok", ton: "warn" };
  return { tekst: "Potvrđeno", ton: "ok" };
}

/** The personnel card's existing sources, reduced to read-only intake context. */
export function InboxWorkerState({ workerId, revision }: { workerId: string; revision: number }) {
  const { can } = useAuth();
  const readCard = can("radnik", "read");
  const readWorker = can("radnik.osnovno", "read") || readCard;
  const readDocuments = can("dokument", "read");
  const readHealthDocuments = can("zdravlje", "read");
  const readHealthStatus = can("zdravlje.status", "read");
  const readPermit = can("dozvola", "read");
  const readTraining = can("znr", "read");
  // Permission changes invalidate the whole snapshot too, including in-flight reads.
  const key = JSON.stringify([workerId, revision, readWorker, readCard, readDocuments, readHealthDocuments, readHealthStatus, readPermit, readTraining]);
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null);

  useEffect(() => {
    if (!readWorker) return;
    const controller = new AbortController();
    async function load() {
      try {
        const worker = await dohvatiRadnika(workerId, controller.signal);
        if (controller.signal.aborted) return;
        const active = worker.aktivan === true && worker.izvor !== "povijesni";
        const [documents, trainings] = await Promise.allSettled([
          readDocuments ? dohvatiDokumente(workerId, controller.signal) : Promise.resolve([]),
          readTraining && active ? dohvatiOsposobljavanja(workerId, controller.signal) : Promise.resolve([]),
        ]);
        if (controller.signal.aborted) return;
        setSnapshot({
          key, worker,
          documents: documents.status === "fulfilled" ? documents.value : [],
          trainings: trainings.status === "fulfilled" ? trainings.value : [],
          errors: [
            ...(documents.status === "rejected" ? ["Arhiva dokumenata nije dostupna."] : []),
            ...(trainings.status === "rejected" ? ["Evidencija ZNR nije dostupna."] : []),
          ],
        });
      } catch {
        if (!controller.signal.aborted) setSnapshot({ key, documents: [], trainings: [], errors: ["Stanje radnika nije dostupno."] });
      }
    }
    void load();
    return () => controller.abort();
  }, [key, workerId, readWorker, readDocuments, readTraining]);

  const current = snapshot?.key === key ? snapshot : null;
  const worker = current?.worker;
  const active = worker?.aktivan === true && worker.izvor !== "povijesni";
  const inactive = worker?.aktivan === false || worker?.izvor === "povijesni";
  const documents = (current?.documents ?? []).filter((document) =>
    document.status !== "deleted" &&
    (!active || document.status === "active") &&
    (readHealthDocuments || !HEALTH_CODES.has(document.document_type_code ?? "")),
  );
  const trainings = (current?.trainings ?? []).filter((record) => !record.povijesno);

  return <section aria-label="Stanje radnika" className="avm-surface flex h-full min-h-0 flex-col rounded-lg border border-slate-200 bg-white">
    <h2 className="shrink-0 border-b border-slate-200 p-3 font-semibold">Stanje radnika</h2>
    <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-3 text-sm">
      {!readWorker ? <p>Nema ovlasti za pregled radnika.</p> : !current ? <p role="status">Učitavanje stanja radnika…</p> : <>
        {current.errors.map((error) => <p role="alert" key={error}>{error}</p>)}
        {worker && <>
          <div><p className="font-semibold">{punoIme(worker)}</p><p className="text-slate-600">{statusRadnika(worker)}</p></div>
          {inactive && <p>Arhivski pregled. Ne prikazuju se obveze i upozorenja aktivnog zaposlenja.</p>}
          <p className="text-xs text-slate-600">Prikaz ovisi o vašim ovlastima. Nepotvrđeni dokumenti iz ladice još nisu dio stanja radnika.</p>
          {active && <div><PropertyList>
            {readPermit && <StateRow title="Dozvola" label={oznakaDozvole(worker.dozvola_status, worker.vrsta_ugovora)} until={worker.dozvola_status?.datum_isteka} />}
            {readHealthStatus && <StateRow title="Liječnički pregled" label={oznakaLijecnickog(worker.zdravstveni_status)} until={worker.zdravstveni_status?.vrijedi_do} />}
            {readCard && <>
              <StateRow title="Ugovor" label={{ tekst: worker.vrsta_ugovora ? VRSTA_UGOVORA[worker.vrsta_ugovora] ?? worker.vrsta_ugovora : "Nema podatka", ton: "idle" }} until={worker.ugovor_vrijedi_do} />
              <StateRow title="Prijava osiguranja" label={{ tekst: worker.datum_prijave_osiguranja ? datum(worker.datum_prijave_osiguranja) : "Nema podatka", ton: "idle" }} />
            </>}
            </PropertyList>
            {readTraining && !current.errors.includes("Evidencija ZNR nije dostupna.") && <div className="space-y-2 py-2">
              <h3 className="font-medium">ZNR — osposobljavanja</h3>
              {trainings.length === 0 ? <p className="text-slate-600">Nema evidentiranih osposobljavanja.</p> : <ul className="space-y-2">{trainings.map((record) => <li key={record.id}>
                <p>{record.vrsta_naziv ?? record.vrsta_izvorni_tekst ?? "Vrsta osposobljavanja nije dostupna"}</p>
                <p className="text-xs text-slate-600">{STATUS_OSPOSOBLJAVANJA[record.status] ?? record.status}{record.datum_osposobljavanja ? ` · ${datum(record.datum_osposobljavanja)}` : ""}</p>
              </li>)}</ul>}
            </div>}
          </div>}
          {active && readCard && worker.uskladenost && !worker.uskladenost.potpuno && <div>
            <h3 className="font-medium">Nedostaje u kartonu</h3>
            <ul className="list-disc space-y-1 pl-4">{worker.uskladenost.nedostaje.map((reason) => <li key={reason}>{reason}</li>)}</ul>
          </div>}
          {readDocuments ? <div>
            <h3 className="mb-2 font-medium">{active ? "Dokumenti u važećoj evidenciji" : "Arhiva dokumenata"}</h3>
            {!current.errors.includes("Arhiva dokumenata nije dostupna.") && (documents.length === 0 ? <p className="text-slate-600">Nema dokumenata dostupnih za prikaz.</p> : <PropertyList>{documents.map((document) => <StateRow key={document.id} title={document.document_type_name ?? document.naziv ?? "Dokument"} label={documentState(document, active)} until={document.expires_on} />
            )}</PropertyList>)}
          </div> : <p className="text-slate-600">Nema ovlasti za pregled arhive dokumenata.</p>}
        </>}
      </>}
    </div>
  </section>;
}
